import { z } from 'zod';
import { Sha256, Ulid } from './common.js';
/**
 * The per-game asset library (docs/asset-library-plan.md).
 *
 * Working material for the playground — sprites, sounds, references — and deliberately not
 * `app_media`, which is the store listing, nor a build, which is the thing that ships. One
 * library per game: a sound effect two games share is uploaded twice, and folders organise
 * within a game rather than across them.
 *
 * Everything here is **declared**. `kind` is derived from the content type the uploader named,
 * not from the bytes; the server never opens an asset, exactly as it never opens a screenshot.
 */
/**
 * A display hint, and only that. Three values rather than a content-type string because the
 * dashboard and the assistant both branch on "can I show this / play this", and neither should
 * be maintaining its own list of MIME types to answer it.
 */
export const AppAssetKind = z.enum(['image', 'audio', 'other']);
/** Who put it there: a person at the dashboard, or the assistant through its MCP tools. */
export const AppAssetOrigin = z.enum(['upload', 'assistant']);
/**
 * The accepted types: **any of them.**
 *
 * This used to be a closed list of thirteen, on the argument that an open one would make the
 * library an anonymous file host with a game attached. The list lost. A game is made out of more
 * than sprites and sounds — a design doc, a spreadsheet of levels, a shader, a `.tsx` somebody
 * wants rewritten — and every one of those is a thing a developer would reasonably hand the
 * assistant. Refusing them meant the composer's file picker was narrower than the conversation
 * it belongs to, and the honest fix is to take what they give us.
 *
 * What replaces the list is the three things that were actually doing the protecting, none of
 * which was the list:
 *
 *  - **A quota.** `ASSET_LIMITS` bounds one file, the row count and the total bytes, and it is
 *    per app rather than per account. That is what stops this being a file host.
 *  - **A shape check on the type itself** ({@link ContentType}), because the declared string is
 *    echoed into a `content-type` response header and a value with a newline in it would be
 *    header injection. That was previously a side effect of the list; now it is the rule.
 *  - **The way the bytes are served.** `GET /v1/assets/:id` sends `sandbox` CSP and
 *    `nosniff`, so an `image/svg+xml` — or now a `text/html` — renders as an inert document
 *    that can reach nothing of BoardFlight's. That header was always the real answer to "an SVG
 *    is a document with script in it"; the closed list was never what made it safe.
 *
 * The bytes are still never opened. `kind` is derived from the type the uploader declared, and
 * a `.png` full of zip is a `.png` as far as everything here is concerned.
 */
/**
 * A declared content type, shaped like one.
 *
 * `type/subtype`, lowercase, no parameters — `text/plain`, not `text/plain; charset=utf-8`. The
 * cap on each half and the character class are RFC 6838's, minus the separators that would let a
 * value break out of the header it is written into. Nothing here asserts the type is *true*, only
 * that it is a content type: a docx declared as `text/plain` is stored as `text/plain` and served
 * as one, which is the uploader's business and no worse than any other declaration in this system.
 */
export const ContentType = z
    .string()
    .transform((value) => value.trim().toLowerCase())
    .refine((value) => /^[a-z0-9][a-z0-9!#$&^_.+-]{0,126}\/[a-z0-9][a-z0-9!#$&^_.+-]{0,126}$/.test(value), {
    message: 'A content type looks like "image/png": one type, one subtype, no parameters and no spaces.',
});
/**
 * The declared content type → the kind stored on the row.
 *
 * A display hint and nothing more (see {@link AppAssetKind}), so it is answered by the prefix
 * rather than by a table: the question the dashboard asks it is "can I put this in an `<img>` /
 * an `<audio>`", and `image/` and `audio/` are exactly what answers that. Everything else is
 * `other`, which is a row with a name on it — the right rendering for a docx, a shader and a
 * font alike. Total, so there is no "not on the list" branch anywhere to be the reason an upload
 * was refused. `agentd`'s `kindFor` is the same three lines, restated there for the reason that
 * file gives.
 */
export function assetKindFor(contentType) {
    const type = contentType.trim().toLowerCase();
    if (type.startsWith('image/'))
        return 'image';
    if (type.startsWith('audio/'))
        return 'audio';
    return 'other';
}
/** The extension an asset's R2 key gets, for the types where guessing from the name is worse. */
const ASSET_EXTENSIONS = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/webp': 'webp',
    'image/svg+xml': 'svg',
    'image/gif': 'gif',
    'audio/wav': 'wav',
    'audio/mpeg': 'mp3',
    'audio/ogg': 'ogg',
    'audio/webm': 'weba',
    'application/json': 'json',
    'text/plain': 'txt',
    'font/ttf': 'ttf',
    'font/woff2': 'woff2',
};
/**
 * The suffix on the R2 key. Cosmetic — the object is found by the key stored on the row, and
 * what it is served as comes from `content_type`, not from this.
 *
 * The table answers the handful of types where the name is the worse source (`.jpeg`/`.jpg`,
 * a `.oga` that is `audio/ogg`), and the filename answers everything else, because with an open
 * type list a table can no longer be complete. Sanitised hard on the way through: this string is
 * concatenated into an R2 key, so it is letters and digits or it is `bin`.
 */
export function assetExtensionFor(contentType, name) {
    const known = ASSET_EXTENSIONS[contentType.trim().toLowerCase()];
    if (known)
        return known;
    const suffix = name?.includes('.') ? name.split('.').pop().toLowerCase() : '';
    return /^[a-z0-9]{1,12}$/.test(suffix) ? suffix : 'bin';
}
/**
 * Three numbers, and each one is a different worry.
 *
 * `maxBytes` is one file: a Board-sized sprite sheet or a music loop, and far below the Worker
 * body limit, which is what lets every asset be proxied rather than presigned (see the route).
 * `maxCount` bounds the listing — the dashboard renders the whole library in one grid and the
 * assistant reads it in one tool result. `maxTotalBytes` is the only one about disk.
 *
 * With the type list open (see above) these three are the *whole* of what bounds a library, so
 * they are load-bearing in a way they were not before. They are per app, which is the part that
 * matters: filling one is filling your own game's library.
 */
export const ASSET_LIMITS = {
    maxBytes: 10 * 1024 * 1024,
    maxCount: 500,
    maxTotalBytes: 256 * 1024 * 1024,
};
/**
 * A folder path. `''` is the root, and there is no trailing slash anywhere.
 *
 * Validated like a project path and for the same reason: it is concatenated into a filesystem
 * path inside the sandbox by `get_asset`, so `..` here would be a jail escape there — the jail
 * catches it, and a value that only fails at the far end is a value that should never have been
 * stored. Control characters are refused because this string is printed into a console row and
 * a tool result.
 */
export const ASSET_FOLDER_MAX = 200;
export const ASSET_NAME_MAX = 120;
export const AssetFolder = z
    .string()
    // A trailing slash is normalised away — every file picker and every person types `sprites/`
    // sometimes, and it unambiguously means the same folder. A *leading* one is refused rather
    // than stripped, because "/sprites" is a claim about a root this path does not have, and the
    // path jail this string eventually reaches answers it the same way.
    .transform((value) => value.trim().replace(/\/+$/, ''))
    .refine((value) => !value.startsWith('/'), {
    message: 'A folder path is relative to the library root, so it does not start with "/".',
})
    .refine((value) => value.length <= ASSET_FOLDER_MAX, {
    message: `A folder path is at most ${ASSET_FOLDER_MAX} characters.`,
})
    .refine((value) => value === '' ||
    value
        .split('/')
        .every((segment) => segment.length > 0 && segment !== '.' && segment !== '..'), { message: 'A folder path has no empty segments and no "." or ".." in it.' })
    // eslint-disable-next-line no-control-regex
    .refine((value) => !/[\x00-\x1f\x7f\\]/.test(value), {
    message: 'A folder path cannot contain control characters or backslashes.',
});
/** A filename with its extension. Unique per folder, which the database enforces. */
export const AssetName = z
    .string()
    .transform((value) => value.trim())
    .refine((value) => value.length > 0 && value.length <= ASSET_NAME_MAX, {
    message: `A name is 1 to ${ASSET_NAME_MAX} characters.`,
})
    .refine((value) => !value.includes('/') && value !== '.' && value !== '..', {
    message: 'A name is one file, not a path.',
})
    // eslint-disable-next-line no-control-regex
    .refine((value) => !/[\x00-\x1f\x7f\\]/.test(value), {
    message: 'A name cannot contain control characters or backslashes.',
});
export const AppAsset = z.object({
    id: Ulid,
    folder: z.string(),
    name: z.string(),
    kind: AppAssetKind,
    contentType: z.string(),
    size: z.number().int(),
    sha256: z.string(),
    origin: AppAssetOrigin,
    /**
     * Public, unguessable and immutable, exactly as a media URL is: an `<img>` and an `<audio>`
     * cannot attach a bearer token, and 80 bits of ULID entropy is the capability.
     */
    url: z.string(),
    createdAt: z.number().int(),
});
export const CreateAppAssetRequest = z.object({
    folder: AssetFolder.optional().default(''),
    name: AssetName,
    contentType: ContentType,
    size: z.number().int().positive(),
    /** Recorded, never verified — the media rule. The device is what checks bytes. */
    sha256: Sha256,
});
export const CreateAppAssetResponse = z.object({
    asset: AppAsset,
    uploadUrl: z.string(),
    method: z.literal('PUT'),
    headers: z.record(z.string(), z.string()),
    expiresAt: z.number().int(),
});
export const UpdateAppAssetRequest = z
    .object({
    folder: AssetFolder.optional(),
    name: AssetName.optional(),
})
    .refine((value) => value.folder !== undefined || value.name !== undefined, {
    message: 'Send a folder, a name, or both.',
});
/**
 * Renaming or moving a folder.
 *
 * A folder is not a row — it exists because an asset names it — so this is a prefix rewrite over
 * the assets inside it, and `to: ''` moves them to the root.
 */
export const MoveAssetFolderRequest = z
    .object({
    from: AssetFolder,
    to: AssetFolder,
})
    .refine((value) => value.from !== '', { message: 'The root folder cannot be moved.' })
    .refine((value) => value.from !== value.to, { message: 'That folder is already there.' })
    .refine((value) => !`${value.to}/`.startsWith(`${value.from}/`), {
    message: 'A folder cannot be moved inside itself.',
});
/** `sprites/knife.png` — how an asset is named in a console row, a tool result and a tooltip. */
export function assetPath(asset) {
    return asset.folder === '' ? asset.name : `${asset.folder}/${asset.name}`;
}
//# sourceMappingURL=assets.js.map